import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { routes } from '@/constants/routes';
import { useRestaurantsContext } from '@/contexts/RestaurantContext';
import { toast } from 'react-toastify';
import Dropdown from '@/components/Dropdown';
import logo from '@/assets/Logo.png';
import { emptyStorage } from '@/utils/emptyStorage';
import axios from 'axios';

const BOLayout = ({ children }) => {
	const role = JSON.parse(localStorage.getItem('role'));
	const email = JSON.parse(localStorage.getItem('user'))?.email;
	const navigate = useNavigate();
	const { restaurants, setRestaurants, selectedRestaurant, setSelectedRestaurant, refreshSelectedRestaurant } = useRestaurantsContext();

	useEffect(() => {
		const fetchData = async () => {
			try {
				const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${email}/list`, {
					params: {
						role,
					},
				});

				if (response.status === 200) {
					setRestaurants(response.data);
					
					// Rafraîchir les données du restaurant sélectionné
					if (selectedRestaurant) {
						await refreshSelectedRestaurant();
					}
				} else {
					toast.error('Une erreur est survenue', {
						position: 'top-center',
						autoClose: 1000,
						hideProgressBar: false,
						closeOnClick: true,
						pauseOnHover: false,
						draggable: true,
						theme: 'colored',
					});
				}
			} catch (error) {
				if (error.response.status === 403) {
					toast.info('Zone interdite. Redirection...', {
						position: 'top-center',
						autoClose: 1000,
						hideProgressBar: false,
						closeOnClick: true,
						pauseOnHover: false,
						draggable: true,
						theme: 'colored',
					});

					// Redirect to login page if the user is logged in but has the wrong access rights
					// Or token has expired
					if (role && email) {
						emptyStorage();

						navigate('/login');
					}
				}
			}
		};

		fetchData();
	}, []);

	const handleFetchRestaurant = async (restaurant) => {
		if (!restaurant) {
			return;
		}

		try {
			const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${restaurant.id}/view`);

			setSelectedRestaurant({
				...response.data,
				name: response.data.name
					.split('-')
					.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
					.join(' '),
			});

			if (response.status === 200) {
				window.location.href = `/back-office/restaurants/${selectedRestaurant?.id}/view`;
			}
		} catch (error) {
			toast.error('Une erreur est survenue', {
				position: 'top-center',
				autoClose: 3500,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}
	};

	const handleLogout = async () => {
		try {
			const response = await axios.post(`${import.meta.env.VITE_API_URL}/api/logout`);
			emptyStorage();

			if (response.status === 200) {
				toast.info('Déconnexion réussie', {
					position: 'top-center',
					autoClose: 1000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});
			}
		} catch (error) {
			console.error('Error logging out user:', error);

			toast.error('Une erreur est survenue', {
				position: 'top-center',
				autoClose: 1000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}

		setTimeout(() => navigate('/'), 1000);
	};

	return (
		<div className="flex h-screen bg-white">
			<div className="bg-primary md:w-[150px] p-6 flex flex-col justify-between lg:w-[282px]">
				<div>
					<div className="grid grid-col-1 gap-4 mb-6">
						<img
							src={logo}
							alt="logo"
							className="w-3/4 mx-auto mb-4 hover:cursor-pointer"
							onClick={() => (window.location.href = routes.home)}
						/>

						<form className='w-full relative'>
							<Dropdown
								buttonText={selectedRestaurant?.name || 'Sélectionnez votre restaurant'}
								placeholder="Sélectionnez votre restaurant"
								options={restaurants}
								isObject={true}
								multiKeys={['name']}
								classes='flex items-center justify-center bg-white w-full rounded-lg px-4 py-2 text-center text-sm'
								onOptionSelect={handleFetchRestaurant}
							/>
						</form>
					</div>

					<ul className="flex flex-col gap-4 text-xl text-white">
						{role && ['super-admin', 'admin'].includes(role) && (
							<li
								onClick={() => navigate('/back-office')}
								className="cursor-pointer transition duration-300 hover:underline hover:text-white/80"
							>
								Dashboard
							</li>
						)}
						{role && ['super-admin', 'admin', 'manager'].includes(role) && (
							<li
								onClick={() => (window.location.href = `/back-office/restaurants/${selectedRestaurant?.id}/commandes`)}
								className="cursor-pointer transition duration-300 hover:underline hover:text-white/80"
							>
								Commandes
							</li>
						)}
						{role && !['manager', 'user'].includes(role) && (
							<>
								<li
									className="cursor-pointer transition duration-300 hover:underline hover:text-white/80"
									onClick={() => (window.location.href = `/back-office/restaurants/${selectedRestaurant?.id}/clients`)}
								>
									Clients
								</li>
								<li
									className="cursor-pointer transition duration-300 hover:underline hover:text-white/80"
									onClick={() => (window.location.href = `/back-office/restaurants/${selectedRestaurant?.id}/promos`)}
								>
									Codes promo
								</li>
								<li
									className="cursor-pointer transition duration-300 hover:underline hover:text-white/80"
									onClick={() => (window.location.href = `/back-office/notifications/list`)}
								>
									Notifications
								</li>


								<li
									onClick={() => (window.location.href = `/back-office/restaurants/${selectedRestaurant?.id}/view`)}
									className="cursor-pointer transition duration-300 hover:underline hover:text-white/80"
								>
									Restaurant
								</li>
								<div
									className="cursor-pointer"
									onClick={() => (window.location.href = `/back-office/restaurants/${selectedRestaurant?.id}/barcodes`)}
								>
									Codes-barres
								</div>
							</>
						)}
						{role && ['super-admin', 'admin'].includes(role) && (
							<li
								onClick={() => navigate('/back-office/statistics')}
								className="cursor-pointer transition duration-300 hover:underline hover:text-white/80"
							>
								Statistiques
							</li>
						)}
						{role && ['super-admin', 'admin'].includes(role) && (
							<li
								onClick={() => navigate('/back-office/stocks')}
								className="cursor-pointer transition duration-300 hover:underline hover:text-white/80"
							>
								Gestion des stocks
							</li>
						)}
					</ul>
				</div>

				<div>
					<ul className="flex flex-col gap-4 text-xl text-white">
						{role && role === 'super-admin' && (
							<li
								className="cursor-pointer transition duration-300 hover:underline hover:text-white/80"
								onClick={() => navigate('/back-office/settings/settingmenu')}
							>
								Paramètres
							</li>
						)}
						<li
							className="cursor-pointer transition duration-300 hover:underline hover:text-white/80"
							onClick={handleLogout}
						>
							Déconnexion
						</li>
					</ul>
				</div>
			</div>

			<div className="overflow-y-auto bg-white w-4/5 px-2 py-8 xl:p-24 mx-auto xl:w-4/5">{children}</div>
		</div>
	);
};

export default BOLayout;
